// Product option modal — Edit-from-cart: rebuild the modal's selection state from an existing cart line.
import { asArray, getModifierCustomGroups } from './normalize';

export function findOptionMatch(options, selected) {
  const selectedIds = [
    selected?.id,
    selected?.variantId,
    selected?.variant_id,
    selected?.modifierId,
    selected?.modifier_id,
  ]
    .filter(Boolean)
    .map(String);
  const selectedNames = [
    selected?.name,
    selected?.label,
    selected?.variantName,
    selected?.variant_name,
    selected?.modifierName,
    selected?.modifier_name,
  ]
    .filter(Boolean)
    .map(String);

  return (
    options.find((option) => selectedIds.includes(String(option.id))) ??
    options.find((option) => selectedNames.includes(String(option.name))) ??
    null
  );
}

export function findGroupForSelection(groups, selected, kind) {
  if (!selected) return null;

  const selectedIds = [
    selected?.id,
    selected?.variantId,
    selected?.variant_id,
    selected?.modifierId,
    selected?.modifier_id,
  ]
    .filter(Boolean)
    .map(String);
  const selectedNames = [
    selected?.name,
    selected?.label,
    selected?.variantName,
    selected?.variant_name,
    selected?.modifierName,
    selected?.modifier_name,
  ]
    .filter(Boolean)
    .map(String);
  const selectedGroupIds = [
    selected?.groupId,
    selected?.group_id,
    selected?.variant_group_id,
    selected?.modifier_group_id,
  ]
    .filter(Boolean)
    .map(String);
  const selectedGroupNames = [
    selected?.groupName,
    selected?.group_name,
    selected?.variant_group_name,
    selected?.modifier_group_name,
  ]
    .filter(Boolean)
    .map(String);

  for (const group of groups) {
    if (selectedGroupIds.includes(String(group.id))) {
      const options = asArray(group.options);
      return {
        group,
        option: options.find((option) => selectedIds.includes(String(option.id))) ?? null,
      };
    }
    if (selectedGroupNames.includes(String(group.name))) {
      const options = asArray(group.options);
      return {
        group,
        option:
          options.find((option) => selectedIds.includes(String(option.id))) ??
          options.find((option) => selectedNames.includes(String(option.name))) ??
          null,
      };
    }

    const options = asArray(group.options);
    const byId = options.find((option) => selectedIds.includes(String(option.id)));
    if (byId) return { group, option: byId };
    const byName = options.find((option) => selectedNames.includes(String(option.name)));
    if (byName) return { group, option: byName };
  }

  if (kind === 'modifier' && groups.some((group) => group.id === 'single-modifiers')) {
    return { group: groups.find((group) => group.id === 'single-modifiers'), option: null };
  }

  return null;
}

export function buildPrefillState(line, variantGroups, modifierGroups) {
  const variantSelections = {};
  const modifierSelections = {};
  const selectedVariants = asArray(line?.selectedVariants);
  const selectedModifiers = asArray(line?.selectedModifiers).filter(
    (modifier) => !(modifier?.parent_modifier_id ?? modifier?.parentModifierId),
  );
  const fallbackVariant = line?.variant ? [line.variant] : [];
  const fallbackModifiers = (Array.isArray(line?.modifiers) ? line.modifiers : []).filter(
    (modifier) => !(modifier?.parent_modifier_id ?? modifier?.parentModifierId),
  );
  let variantChosen = false;

  for (const group of variantGroups) {
    if (variantChosen) break;
    const selected =
      selectedVariants.find(
        (option) =>
          (option.groupId && option.groupId === group.id) ||
          (option.groupName && option.groupName === group.name),
      ) ??
      fallbackVariant[0] ??
      null;
    const match = selected ? findOptionMatch(group.options, selected) : null;
    if (match) {
      variantSelections[group.id] = match.id;
      variantChosen = true;
      continue;
    }

    const grouped = findGroupForSelection(variantGroups, selected, 'variant');
    if (grouped?.group?.id === group.id && grouped.option) {
      variantSelections[group.id] = grouped.option.id;
      variantChosen = true;
    }
  }

  for (const selected of selectedModifiers) {
    const grouped = findGroupForSelection(modifierGroups, selected, 'modifier');
    if (!grouped?.group) continue;
    const groupSelection = modifierSelections[grouped.group.id] || {};
    const match = grouped.option ?? findOptionMatch(grouped.group.options, selected);
    if (!match) continue;
    groupSelection[match.id] = Math.max(1, Number(selected.quantity ?? 1));
    modifierSelections[grouped.group.id] = groupSelection;
  }

  for (const selected of fallbackModifiers) {
    const grouped = findGroupForSelection(modifierGroups, selected, 'modifier');
    if (!grouped?.group) continue;
    const groupSelection = modifierSelections[grouped.group.id] || {};
    const match = grouped.option ?? findOptionMatch(grouped.group.options, selected);
    if (!match) continue;
    groupSelection[match.id] = Math.max(1, Number(selected.quantity ?? 1));
    modifierSelections[grouped.group.id] = groupSelection;
  }

  for (const group of modifierGroups) {
    const groupSelection = {};
    const groupSelected = [
      ...selectedModifiers.filter(
        (option) =>
          (option.groupId && option.groupId === group.id) ||
          (option.groupName && option.groupName === group.name),
      ),
    ];

    for (const selected of groupSelected) {
      const match = findOptionMatch(group.options, selected);
      if (match) {
        groupSelection[match.id] = Math.max(1, Number(selected.quantity ?? 1));
      }
    }

    if (Object.keys(groupSelection).length > 0) {
      modifierSelections[group.id] = groupSelection;
    }
  }

  return { variantSelections, modifierSelections };
}

export function buildModifierCustomPrefillState(line, modifierGroups) {
  const state = {};
  const modifierList = asArray(line?.selectedModifiers).length
    ? asArray(line.selectedModifiers)
    : asArray(line?.modifiers);
  const childModifiers = modifierList.filter((modifier) =>
    Boolean(modifier?.parent_modifier_id ?? modifier?.parentModifierId),
  );

  for (const child of childModifiers) {
    const parentId = String(child.parent_modifier_id ?? child.parentModifierId);
    for (const group of modifierGroups) {
      const parent = group.options.find(
        (option) => String(option.modifier_id ?? option.id) === parentId,
      );
      if (!parent) continue;

      const parentKey = `${group.id}::${parent.id}`;
      const current = state[parentKey] || { alternate: null, extras: {} };
      const custom = getModifierCustomGroups(parent);
      const childId = String(child.modifier_id ?? child.modifierId ?? child.id ?? '');
      const childUpsellId = String(child.upsell_item_id ?? child.upsellItemId ?? '');

      const alternate = custom.alternateModifiers.find(
        (option) => String(option.modifier_id ?? option.id) === childId,
      );
      if (alternate) {
        state[parentKey] = {
          alternate: { ...alternate, quantity: Math.max(1, Number(child.quantity ?? 1)) },
          extras: {},
        };
        break;
      }

      for (const extraGroup of custom.extraGroups) {
        const extra = extraGroup.items.find(
          (item) =>
            String(item.upsell_item_id ?? item.id) === childUpsellId ||
            String(item.modifier_id ?? item.id) === childId,
        );
        if (!extra) continue;
        current.extras[extraGroup.id] = {
          ...extra,
          quantity: Math.max(1, Number(child.quantity ?? 1)),
        };
        current.alternate = null;
        state[parentKey] = current;
        break;
      }
      break;
    }
  }

  return state;
}

// Mirrors buildPrefillState, but for combo picks. Reads whichever of
// selectedComboItems / comboSelections the saved line carries (same
// primary/legacy-dup pairing used for variant/modifier arrays) and reconstructs
// per-position quantity + active swap + nested modifier state.
export function buildComboPrefillState(line, comboGroups) {
  const positionState = {};
  const componentModifierSelections = {};
  const entries = asArray(line?.selectedComboItems).length
    ? line.selectedComboItems
    : asArray(line?.comboSelections);

  for (const entry of entries) {
    const groupId = entry?.group_id ?? entry?.groupId;
    const parentItemId = entry?.parent_item_id ?? entry?.parentItemId;
    const itemId = entry?.item_id ?? entry?.itemId;

    const group =
      comboGroups.find((g) => g.id === groupId) ??
      comboGroups.find((g) => g.positions.some((p) => p.itemId === (parentItemId ?? itemId)));
    if (!group) continue;

    const position = parentItemId
      ? group.positions.find((p) => p.itemId === parentItemId)
      : group.positions.find((p) => p.itemId === itemId);
    if (!position) continue;

    let swapKey = null;
    if (parentItemId) {
      const variantIds = asArray(entry?.variants)
        .map((v) => v?.variant_id ?? v?.variantId)
        .filter(Boolean)
        .map(String);
      const chip = position.chips.find(
        (c) =>
          c.alternativeItemId === itemId &&
          (variantIds.length === 0 || variantIds.includes(String(c.variantId))),
      );
      swapKey = chip?.key ?? null;
    }

    positionState[position.id] = {
      quantity: Math.max(1, Number(entry?.quantity ?? 1)),
      swapKey,
    };

    const componentKey = `${position.id}::${swapKey || 'default'}`;
    const modifierSel = {};
    for (const mod of asArray(entry?.modifiers)) {
      const modId = mod?.modifier_id ?? mod?.modifierId;
      if (!modId) continue;
      modifierSel[modId] = Math.max(1, Number(mod?.quantity ?? 1));
    }
    if (Object.keys(modifierSel).length > 0) {
      componentModifierSelections[componentKey] = modifierSel;
    }
  }

  return { positionState, componentModifierSelections };
}

export function buildIngredientPrefillKeys(line) {
  const removed = asArray(line?.removedIngredients);
  return new Set(removed.map((r) => String(r?.ingredient_id ?? r?.ingredientId ?? r?.name ?? '')));
}

export function buildServiceItemPrefillState(line) {
  const state = {};
  const selectedGroupScopes = new Set();
  const serviceItems = asArray(line?.serviceItems).length
    ? asArray(line.serviceItems)
    : asArray(line?.service_items);

  for (const item of serviceItems) {
    const serviceItemId = item?.service_item_id ?? item?.serviceItemId;
    const variantId = item?.variant_id ?? item?.variantId;
    const variantGroupId = item?.variant_group_id ?? item?.variantGroupId ?? '';
    const serviceItemGroupId = item?.service_item_group_id ?? item?.serviceItemGroupId ?? '';
    const alternativeUnit = item?.alternative_unit ?? item?.alternativeUnit ?? '';
    if (!serviceItemId) continue;
    if (!variantId && alternativeUnit) {
      state[`alternative-unit::${alternativeUnit}::${serviceItemGroupId}::${serviceItemId}`] = true;
      continue;
    }
    if (!variantId) continue;
    const baseScope = `${variantGroupId || 'single-variants'}::${variantId}::`;
    const groupScope = serviceItemGroupId ? `${baseScope}${serviceItemGroupId}::` : '';
    if (groupScope && selectedGroupScopes.has(groupScope)) continue;
    if (groupScope) selectedGroupScopes.add(groupScope);
    state[`${baseScope}${serviceItemGroupId}::${serviceItemId}`] = true;
  }

  return state;
}

export function toPayloadGroupIdForSelection(groupId) {
  return groupId === 'single-variants' ? null : groupId;
}
